import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Building2, MapPin, Wallet, TrendingUp, TrendingDown, CalendarDays, Edit3, Phone, User, ClipboardList } from "lucide-react";

const TAB = { overview: "overview", bookings: "bookings", expenses: "expenses" };

export default function PropertyDetail() {
  const { t, lang } = useLang();
  const { format: money } = useCurrency();
  const { id } = useParams();
  const navigate = useNavigate();
  const [prop, setProp] = useState(null);
  const [locations, setLocations] = useState({});
  const [summary, setSummary] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [tab, setTab] = useState(TAB.overview);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const [p, l, s, b, e] = await Promise.all([
          api.get(`/properties/${id}`),
          api.get("/oman/locations"),
          api.get(`/reports/summary?period=year&property_id=${id}`),
          api.get(`/bookings?property_id=${id}`),
          api.get(`/expenses?property_id=${id}`),
        ]);
        setProp(p.data); setLocations(l.data); setSummary(s.data);
        setBookings(b.data); setExpenses(e.data);
      } catch (err) {
        toast.error(formatApiError(err));
        if (err?.response?.status === 404) navigate("/app/properties");
      }
    })();
  }, [id, navigate]);

  const totals = useMemo(() => {
    const income = bookings.filter((b) => b.status === "rented").reduce((s, b) => s + (Number(b.amount) || 0), 0);
    const expense = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    return { income, expense, profit: income - expense, count: bookings.length };
  }, [bookings, expenses]);

  if (!prop) return <div className="text-[#5C6053]">…</div>;

  const govAr = locations[prop.governorate]?.ar;
  const wilayatIdx = locations[prop.governorate]?.wilayats?.indexOf(prop.wilayat);
  const wilayatAr = wilayatIdx > -1 ? locations[prop.governorate]?.wilayats_ar?.[wilayatIdx] : null;
  const govLabel = lang === "ar" ? (govAr || prop.governorate) : prop.governorate;
  const wilayatLabel = lang === "ar" ? (wilayatAr || prop.wilayat) : prop.wilayat;

  return (
    <div className="space-y-5" data-testid="property-detail-page">
      <div className="bg-white border border-[#E5E1D8] rounded-2xl p-5 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="w-10 h-10 rounded-lg bg-[#F3EFE7] text-[#2A5948] flex items-center justify-center flex-shrink-0"><Building2 size={18} /></div>
              <h1 className="text-2xl sm:text-3xl font-semibold truncate">{prop.name}</h1>
              <span className="text-xs px-2 py-0.5 rounded bg-[#F3EFE7] text-[#5C6053]">{t(`type_${prop.type}`)}</span>
            </div>
            <div className="mt-2 text-sm text-[#5C6053] flex items-center gap-1">
              <MapPin size={12} /> {govLabel} — {wilayatLabel}
              {prop.village ? ` — ${prop.village}` : ""}
            </div>
          </div>
          <div className="flex gap-2">
            <Link data-testid="property-open-calendar" to={`/app/calendar`} className="px-3 py-2 rounded-lg border border-[#E5E1D8] hover:bg-[#F3EFE7] text-sm inline-flex items-center gap-2">
              <CalendarDays size={14} /> {t("calendar")}
            </Link>
            <Link data-testid="property-edit-btn" to={`/app/property/${prop.id}/edit`} className="brand-btn px-3 py-2 rounded-lg text-sm inline-flex items-center gap-2">
              <Edit3 size={14} /> {t("edit")}
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat testid="pd-income" icon={TrendingUp} label={t("income")} value={money(totals.income)} tone="green" />
        <Stat testid="pd-expense" icon={TrendingDown} label={t("expense")} value={money(totals.expense)} tone="red" />
        <Stat testid="pd-profit" icon={Wallet} label={t("profit")} value={money(totals.profit)} tone="brand" />
        <Stat testid="pd-bookings-count" icon={ClipboardList} label={t("total_bookings")} value={totals.count} tone="accent" />
      </div>

      <div className="flex gap-1 p-1 bg-white border border-[#E5E1D8] rounded-xl w-fit">
        {Object.values(TAB).map((tKey) => (
          <button
            key={tKey}
            data-testid={`pd-tab-${tKey}`}
            onClick={() => setTab(tKey)}
            className={`px-4 py-2 text-sm rounded-lg ${tab === tKey ? "bg-[#2A5948] text-white" : "text-[#1C1E17] hover:bg-[#F3EFE7]"}`}
          >
            {t(`pd_${tKey}`)}
          </button>
        ))}
      </div>

      {tab === TAB.overview && (
        <div className="bg-white border border-[#E5E1D8] rounded-2xl p-5 shadow-sm space-y-4" data-testid="pd-overview">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <Info label={t("property_name")} value={prop.name} />
            <Info label={t("property_type")} value={t(`type_${prop.type}`)} />
            <Info label={t("governorate")} value={govLabel} />
            <Info label={t("wilayat")} value={wilayatLabel} />
            <Info label={t("village")} value={prop.village || "—"} />
            <Info label="Daily rate" value={money(prop.daily_rate || 0)} />
            <Info label="Hourly rate" value={money(prop.hourly_rate || 0)} />
          </div>
        </div>
      )}

      {tab === TAB.bookings && (
        <div className="bg-white border border-[#E5E1D8] rounded-2xl shadow-sm overflow-x-auto" data-testid="pd-bookings">
          <table className="w-full text-sm">
            <thead className="bg-[#F3EFE7] text-[#5C6053] text-xs uppercase">
              <tr>
                <th className="text-start px-4 py-3">{t("date")}</th>
                <th className="text-start px-4 py-3">{t("guest_name")}</th>
                <th className="text-start px-4 py-3">{t("guest_phone")}</th>
                <th className="text-start px-4 py-3">{t("status_rented")}</th>
                <th className="text-start px-4 py-3">{t("amount")}</th>
                <th className="text-start px-4 py-3">{t("deposit")}</th>
                <th className="text-start px-4 py-3">{t("insurance")}</th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#5C6053]">—</td></tr>}
              {bookings.slice().sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((b) => (
                <tr key={b.id} data-testid={`pd-booking-${b.id}`} className="border-t border-[#E5E1D8]">
                  <td className="px-4 py-3">{b.date}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1"><User size={12} className="text-[#5C6053]" />{b.guest_name || "—"}</span></td>
                  <td className="px-4 py-3" dir="ltr">
                    {b.guest_phone ? <a href={`tel:${b.guest_phone}`} className="text-[#2A5948] hover:underline inline-flex items-center gap-1"><Phone size={12} />{b.guest_phone}</a> : "—"}
                  </td>
                  <td className="px-4 py-3"><span className={`text-[10px] px-2 py-0.5 rounded ${b.status === "rented" ? "bg-[#E6F1E7] text-[#2A5948]" : "bg-[#FFF3D6] text-[#8A6D1F]"}`}>{t(`status_${b.status}`)}</span></td>
                  <td className="px-4 py-3">{b.amount || 0}</td>
                  <td className="px-4 py-3">{b.deposit || 0}</td>
                  <td className="px-4 py-3">{b.insurance || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === TAB.expenses && (
        <div className="bg-white border border-[#E5E1D8] rounded-2xl shadow-sm overflow-x-auto" data-testid="pd-expenses">
          <table className="w-full text-sm">
            <thead className="bg-[#F3EFE7] text-[#5C6053] text-xs uppercase">
              <tr>
                <th className="text-start px-4 py-3">{t("date")}</th>
                <th className="text-start px-4 py-3">{t("category")}</th>
                <th className="text-start px-4 py-3">{t("amount")}</th>
                <th className="text-start px-4 py-3">{t("notes")}</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-[#5C6053]">—</td></tr>}
              {expenses.map((e) => (
                <tr key={e.id} data-testid={`pd-expense-${e.id}`} className="border-t border-[#E5E1D8]">
                  <td className="px-4 py-3">{e.date}</td>
                  <td className="px-4 py-3">{t(`cat_${e.category}`) === `cat_${e.category}` ? e.category : t(`cat_${e.category}`)}</td>
                  <td className="px-4 py-3">{money(e.amount)}</td>
                  <td className="px-4 py-3 text-[#5C6053]">{e.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, testid }) {
  const map = {
    green: "bg-[#E6F1E7] text-[#2A5948]",
    red: "bg-[#FBE9E4] text-[#A85540]",
    brand: "bg-[#2A5948] text-white",
    accent: "bg-[#FFF3D6] text-[#8A6D1F]",
  };
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E1D8] rounded-2xl p-4 shadow-sm">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${map[tone]}`}>
        <Icon size={18} />
      </div>
      <div className="mt-3 text-xs text-[#5C6053]">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs text-[#5C6053]">{label}</div>
      <div className="mt-0.5 font-medium">{value}</div>
    </div>
  );
}
